/**
 * @fileoverview Auth provider with Supabase session management and PIN-based user switching.
 * The Supabase auth session belongs to the admin/supervisor who logged in with email+password.
 * Workers switch the app-level user context via badge PIN without changing the auth session.
 */
"use client";

import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { createBrowserClient } from "@/lib/supabase/client";
import { AuthContext } from "@/hooks/auth/useAuth";
import { AuthState, AuthUser, User } from "@/types/auth";
import { UserAccountWithAccount } from "@/types/accounts";
import { Warehouse } from "@/types/warehouse";
import { useRouter } from "next/navigation";

const SWITCHED_USER_KEY = "podhero_switched_user";
const LAST_ACTIVITY_KEY = "podhero_last_activity";
const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const ACTIVITY_CHECK_INTERVAL_MS = 30_000; // 30 seconds

/**
 * @description Build an AuthUser object from a Supabase auth user + their accounts/warehouses.
 */
const createUserObject = async (user: User, supabase: ReturnType<typeof createBrowserClient>): Promise<AuthUser> => {
  let accounts: UserAccountWithAccount[] = [];
  let warehouse_id: string | null = null;
  let warehouse_timezone: string | null = null;
  let can_access_all_warehouses = false;
  let warehouses: Warehouse[] | undefined;

  try {
    const { data: userAccountsData, error: userAccountsError } = await supabase
      .from("user_accounts")
      .select("*, accounts!inner(id, name, slug, is_active)")
      .eq("user_id", user.id);

    if (userAccountsError) {
      console.error("❌ Error fetching user accounts:", userAccountsError);
      throw userAccountsError;
    }

    accounts = userAccountsData || [];

    // Load warehouse assignment from first account
    if (accounts.length > 0) {
      const firstAccount = accounts[0];
      warehouse_id = firstAccount.warehouse_id || null;
      can_access_all_warehouses = firstAccount.can_access_all_warehouses || false;

      // If super user, load all available warehouses (global)
      if (can_access_all_warehouses) {
        const { data: warehousesData, error: warehousesError } = await supabase
          .from("warehouses")
          .select("*")
          .eq("is_active", true)
          .order("name");

        if (!warehousesError && warehousesData) {
          warehouses = warehousesData;

          // Check localStorage for warehouse preference
          const storedWarehouseId = localStorage.getItem(`selected_warehouse_${user.id}`);
          if (storedWarehouseId && warehouses.some(w => w.id === storedWarehouseId)) {
            warehouse_id = storedWarehouseId;
            const selectedWarehouse = warehouses.find(w => w.id === storedWarehouseId);
            warehouse_timezone = selectedWarehouse?.timezone || null;
          } else if (!warehouse_id && warehouses.length > 0) {
            warehouse_id = warehouses[0].id;
            warehouse_timezone = warehouses[0].timezone || null;
          }
        }
      } else if (warehouse_id) {
        const { data: warehouseData } = await supabase
          .from("warehouses")
          .select("timezone")
          .eq("id", warehouse_id)
          .single();
        warehouse_timezone = warehouseData?.timezone || null;
      }
    }

    return {
      id: user.id,
      email: user.email || "",
      name:
        user.user_metadata.display_name ||
        `${user.user_metadata.first_name || ""} ${user.user_metadata.last_name || ""}`.trim() ||
        user.email ||
        "Unknown User",
      phone: user.phone || "",
      user_metadata: user.user_metadata,
      accounts,
      warehouse_id,
      warehouse_timezone,
      can_access_all_warehouses,
      warehouses,
    };
  } catch (error) {
    console.error("❌ Error fetching user accounts:", error);
    return {
      id: user.id,
      email: user.email || "",
      name:
        user.user_metadata.display_name ||
        `${user.user_metadata.first_name || ""} ${user.user_metadata.last_name || ""}`.trim() ||
        user.email ||
        "Unknown User",
      phone: user.phone || "",
      user_metadata: user.user_metadata,
      accounts,
      warehouse_id: null,
      warehouse_timezone: null,
      can_access_all_warehouses: false,
    };
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    isAuthenticated: false,
  });
  const [isSwitchedUser, setIsSwitchedUser] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const router = useRouter();
  const supabase = useMemo(() => createBrowserClient(), []);
  const lastActivityRef = useRef<number>(Date.now());

  /**
   * @description Update last activity timestamp (called on user interaction).
   */
  const touchActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    } catch {
      // localStorage might not be available
    }
  }, []);

  // Track user activity for inactivity lock
  useEffect(() => {
    const events = ["click", "keydown", "touchstart", "scroll"];
    const handler = () => touchActivity();

    events.forEach((e) => document.addEventListener(e, handler, { passive: true }));
    return () => {
      events.forEach((e) => document.removeEventListener(e, handler));
    };
  }, [touchActivity]);

  // Inactivity lock check interval
  useEffect(() => {
    if (!isSwitchedUser) return;

    const interval = setInterval(() => {
      const lastActivity = lastActivityRef.current;
      if (Date.now() - lastActivity > INACTIVITY_TIMEOUT_MS) {
        setIsLocked(true);
      }
    }, ACTIVITY_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isSwitchedUser]);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        setAuthState((prev) => ({ ...prev, loading: true }));

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          // Check for switched user in localStorage
          const switchedUserStr = localStorage.getItem(SWITCHED_USER_KEY);
          if (switchedUserStr) {
            try {
              const switchedUser: AuthUser = JSON.parse(switchedUserStr);
              setAuthState({ user: switchedUser, loading: false, isAuthenticated: true });
              setIsSwitchedUser(true);

              // Only lock if last activity was stale (>10min ago)
              const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
              const lastActivity = lastActivityStr ? parseInt(lastActivityStr, 10) : 0;
              const isStale = Date.now() - lastActivity > INACTIVITY_TIMEOUT_MS;
              setIsLocked(isStale);
              if (!isStale) {
                lastActivityRef.current = lastActivity;
              }
              return;
            } catch {
              localStorage.removeItem(SWITCHED_USER_KEY);
            }
          }

          const user = await createUserObject(session.user, supabase);
          setAuthState({ user, loading: false, isAuthenticated: true });
        } else {
          setAuthState({ user: null, loading: false, isAuthenticated: false });
        }
      } catch (error) {
        console.error("❌ Auth initialization error:", error);
        setAuthState({ user: null, loading: false, isAuthenticated: false });
      }
    };

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("🔄 Auth state changed:", event);

      try {
        if (event === "SIGNED_IN" && session?.user) {
          setTimeout(async () => {
            const user = await createUserObject(session.user, supabase);
            setAuthState({ user, loading: false, isAuthenticated: true });
          }, 0);
        } else if (event === "SIGNED_OUT") {
          // Clear switched user on sign out
          localStorage.removeItem(SWITCHED_USER_KEY);
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          setIsSwitchedUser(false);
          setIsLocked(false);
          setAuthState({ user: null, loading: false, isAuthenticated: false });
          router.push("/login");
        } else if (event === "USER_UPDATED" && session) {
          setTimeout(async () => {
            console.log("update user session");
            const user = await createUserObject(session.user, supabase);
            setAuthState({ user, loading: false, isAuthenticated: true });
          }, 0);
        }
      } catch (error) {
        console.error("❌ Error handling auth state change:", error);
        setAuthState((prev) => ({ ...prev, loading: false }));
      }
    });

    initAuth();

    return () => {
      subscription?.unsubscribe();
    };
  }, [supabase, router]);

  const signIn = useCallback(async (email: string, password: string): Promise<{ error?: string }> => {
    setAuthState((prev) => ({ ...prev, loading: true }));
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setAuthState((prev) => ({ ...prev, loading: false }));
      return { error: error.message };
    }

    return {};
  }, []);

  const signOut = useCallback(async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        console.log("No active session to sign out from");
        localStorage.removeItem(SWITCHED_USER_KEY);
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        setIsSwitchedUser(false);
        setIsLocked(false);
        setAuthState({ user: null, loading: false, isAuthenticated: false });
        router.push("/login");
        return;
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        if (error.message?.includes("session") || error.message?.includes("Session")) {
          console.log("Session already cleared");
          localStorage.removeItem(SWITCHED_USER_KEY);
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          setIsSwitchedUser(false);
          setIsLocked(false);
          setAuthState({ user: null, loading: false, isAuthenticated: false });
          router.push("/login");
          return;
        }
        throw error;
      }
    } catch (error) {
      console.error("Sign out error:", error);
      localStorage.removeItem(SWITCHED_USER_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      setIsSwitchedUser(false);
      setIsLocked(false);
      setAuthState({ user: null, loading: false, isAuthenticated: false });
      router.push("/login");
    }
  }, [router]);

  /**
   * @description Switch the active user by badge PIN.
   * Calls the switch-user API, updates context, persists to localStorage.
   * Does NOT change the Supabase auth session.
   */
  const switchUser = useCallback(async (pin: string): Promise<{ error?: string }> => {
    try {
      const res = await fetch("/api/auth/switch-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        return { error: data.error || "Switch failed" };
      }

      const switchedUser: AuthUser = data.user;

      // Persist to localStorage
      localStorage.setItem(SWITCHED_USER_KEY, JSON.stringify(switchedUser));
      touchActivity();

      // Update auth state
      setAuthState({ user: switchedUser, loading: false, isAuthenticated: true });
      setIsSwitchedUser(true);
      setIsLocked(false);

      return {};
    } catch (error) {
      console.error("Switch user error:", error);
      return { error: "Failed to switch user" };
    }
  }, [touchActivity]);

  /**
   * @description Manually trigger the lock screen.
   */
  const lockScreen = useCallback(() => {
    if (isSwitchedUser) {
      setIsLocked(true);
    }
  }, [isSwitchedUser]);

  const updateUser = useCallback(async (first_name: string, last_name: string) => {
    const { error } = await supabase.auth.updateUser({
      data: {
        first_name,
        last_name,
        display_name: `${first_name} ${last_name}`.trim(),
      },
    });

    if (error) {
      console.error("❌ Error updating user metadata:", error);
      throw error;
    }
  }, []);

  const switchWarehouse = useCallback(async (warehouseId: string) => {
    if (!authState.user?.can_access_all_warehouses) {
      throw new Error("Only super users can switch warehouses");
    }

    if (!authState.user.warehouses?.some(w => w.id === warehouseId)) {
      throw new Error("Invalid warehouse ID");
    }

    localStorage.setItem(`selected_warehouse_${authState.user.id}`, warehouseId);

    setAuthState(prev => ({
      ...prev,
      user: prev.user ? { ...prev.user, warehouse_id: warehouseId } : null,
    }));

    window.location.reload();
  }, [authState.user]);

  const contextValue = useMemo(
    () => ({
      user: authState.user,
      loading: authState.loading,
      isAuthenticated: authState.isAuthenticated,
      signIn,
      signOut,
      updateUser,
      switchWarehouse,
      switchUser,
      isSwitchedUser,
      isLocked,
      lockScreen,
    }),
    [authState, signIn, signOut, updateUser, switchWarehouse, switchUser, isSwitchedUser, isLocked, lockScreen]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
